import type { AttributeMap } from "../types";

/**
 * Builds an AtomicAssets ATTRIBUTE_MAP from a plain string map, for the
 * common case of text-only display attributes (name, description, img,
 * url, ...). AtomicAssets' on-chain ABI wants a map's value encoded as a
 * ["type", value] variant tuple, and the map itself as an array of
 * {key, value} objects — confirmed against a real AtomicAssets JS
 * integration example while building this, not guessed.
 *
 * This only covers "string"-typed attributes. If a schema defines a
 * numeric/bool/image-array attribute etc., extend this (or build the
 * entry manually) with the matching type name instead of "string".
 */
export function toStringAttributeMap(fields: Record<string, string>): AttributeMap {
  return Object.entries(fields)
    .filter(([, value]) => value !== undefined && value !== "")
    .map(([key, value]) => ({ key, value: ["string", value] as [string, string] }));
}

/**
 * Same idea as toStringAttributeMap, but for a schema with mixed field
 * types (e.g. a game item that has string name/img/description plus
 * numeric lookup fields like `level` or `royaltybps`). Pass the ABI type
 * name AtomicAssets expects for each field ("string", "uint64", "uint8",
 * "bool", "image", "float", ...) alongside its value; the value tuple this
 * produces is exactly what createtempl/mintasset's immutable_data param
 * wants. Undefined values are dropped (same "skip if not provided"
 * behavior as toStringAttributeMap) so callers can pass an optional field
 * (e.g. an image that hasn't been uploaded yet) without special-casing it.
 */
export function toAttributeMap(
  fields: Record<string, { type: string; value: string | number | boolean } | undefined>
): AttributeMap {
  return Object.entries(fields)
    .filter((entry): entry is [string, { type: string; value: string | number | boolean }] => {
      const field = entry[1];
      return field !== undefined && field.value !== undefined && field.value !== "";
    })
    .map(([key, field]) => ({ key, value: [field.type, field.value] as [string, string | number | boolean] }));
}
